import { useState } from 'react'
import { Button } from '../../components/primitives/Button'
import { getAllBalancesMatrix } from '../../api/leave-credits'
import { downloadCsv } from '../../lib/leaves-csv'
import { balancesMatrixToCsv } from './balances-csv'
import type { RosterMetric } from './matrix-cell'

/**
 * "Export" control for the Leave Remaining Balance roster. Pulls EVERY employee
 * (all pages, not just the visible one) for the current year and search, builds
 * a CSV in the same column layout as the on-screen table, and downloads it.
 *
 * The fetch is on demand (button click), not part of the page load, so opening
 * the tab stays cheap; a large roster only pages through when HR actually asks
 * for the file.
 */

interface BalancesExportButtonProps {
  metric: RosterMetric
  year: number
  /** Debounced employee-name search — the export matches what's filtered. */
  search: string
  testIdPrefix: string
}

function dateStamp(): string {
  return new Date().toISOString().slice(0, 10)
}

export function BalancesExportButton({
  metric,
  year,
  search,
  testIdPrefix,
}: BalancesExportButtonProps): JSX.Element {
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleExport = async (): Promise<void> => {
    setIsExporting(true)
    setError(null)
    try {
      const rows = await getAllBalancesMatrix({
        year,
        ...(search ? { q: search } : {}),
      })
      const csv = balancesMatrixToCsv(rows, metric)
      const suffix = search ? `-${year}-filtered` : `-${year}`
      downloadCsv(`leave-remaining-balance${suffix}-${dateStamp()}.csv`, csv)
    } catch {
      setError('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
      {/* Solid primary button, matching the Entitlement tab's header CTAs. */}
      <Button
        variant="primary"
        loading={isExporting}
        onClick={() => void handleExport()}
        data-testid={`${testIdPrefix}-export`}
        title={
          search
            ? 'Export the filtered balances as CSV'
            : 'Export all employee balances as CSV'
        }
      >
        {search ? 'Export (filtered)' : 'Export'}
      </Button>
      {error ? (
        <span
          role="alert"
          style={{ fontSize: 11, color: 'var(--danger-fg)' }}
          data-testid={`${testIdPrefix}-export-error`}
        >
          {error}
        </span>
      ) : null}
    </div>
  )
}
